/**
 * API endpoint tanımları
 * Backend route'ları ile frontend'in konuştuğu tek yer
 */

import { API_VERSION } from './api.config';

// Base path'ler
const AUTH_BASE = `/auth`;
const ALARMS_BASE = `/${API_VERSION}/alarms`;
const UNITS_BASE = `/${API_VERSION}/units`;
const WAGONS_BASE = `/${API_VERSION}/wagons`;
const FLEETS_BASE = `/${API_VERSION}/fleets`;
const USERS_BASE = `/${API_VERSION}/users`;
const TENANTS_BASE = `/${API_VERSION}/tenants`;
const BUILDINGS_BASE = `/${API_VERSION}/buildings`;
const NOTIFICATIONS_BASE = `/${API_VERSION}/notifications`;

export const ENDPOINTS = {
  // Authentication endpoints
  AUTH: {
    LOGIN: `${AUTH_BASE}/login`,
    REGISTER: `${AUTH_BASE}/register`,
    LOGOUT: `${AUTH_BASE}/logout`,
    REFRESH_TOKEN: `${AUTH_BASE}/refresh`,
    FORGOT_PASSWORD: `${AUTH_BASE}/forgot-password`,
    RESET_PASSWORD: `${AUTH_BASE}/reset-password`,
    VERIFY_EMAIL: `${AUTH_BASE}/verify-email`,
    PROFILE: `${AUTH_BASE}/profile`,
    CHANGE_PASSWORD: `${AUTH_BASE}/change-password`
  },

  // Alarm endpoints (mevcut projende AlarmDetail, AlarmHistory var)
  ALARMS: {
    BASE: ALARMS_BASE,
    LIST: ALARMS_BASE,
    DETAIL: (id: string | number) => `${ALARMS_BASE}/${id}`,
    BULK: `${ALARMS_BASE}/bulk`,
    STATISTICS: `${ALARMS_BASE}/statistics`,
    HISTORY: (id: string | number) => `${ALARMS_BASE}/${id}/history`,
    RELATED: (id: string | number) => `${ALARMS_BASE}/${id}/related`,
    ACKNOWLEDGE: (id: string | number) => `${ALARMS_BASE}/${id}/acknowledge`,
    RESOLVE: (id: string | number) => `${ALARMS_BASE}/${id}/resolve`,
    FILTERS: `${ALARMS_BASE}/filters`,
    EXPORT: `${ALARMS_BASE}/export`
  },

  // Unit endpoints (mevcut projende Unit, UnitGraph, UnitDiagnostics var)
  UNITS: {
    BASE: UNITS_BASE,
    LIST: UNITS_BASE,
    DETAIL: (id: string | number) => `${UNITS_BASE}/${id}`,
    CREATE: UNITS_BASE,
    UPDATE: (id: string | number) => `${UNITS_BASE}/${id}`,
    DELETE: (id: string | number) => `${UNITS_BASE}/${id}`,
    WAGONS: (id: string | number) => `${UNITS_BASE}/${id}/wagons`,
    ALARMS: (id: string | number) => `${UNITS_BASE}/${id}/alarms`,
    DIAGNOSTICS: (id: string | number) => `${UNITS_BASE}/${id}/diagnostics`,
    GRAPHS: (id: string | number) => `${UNITS_BASE}/${id}/graphs`,
    EXPORT: (id: string | number) => `${UNITS_BASE}/${id}/export`,
    EXPORTALL: `${UNITS_BASE}/export`,
    PERFORMANCE: (id: string | number) => `${UNITS_BASE}/${id}/performance`,
    ATTENTION_REQUIRED: `${UNITS_BASE}/attention-required`
  },

  // Wagon endpoints (mevcut projende WagonDetails, BogieComponent var)
  WAGONS: {
    BASE: WAGONS_BASE,
    LIST: WAGONS_BASE,
    DETAIL: (id: string | number) => `${WAGONS_BASE}/${id}`,
    CREATE: WAGONS_BASE,
    UPDATE: (id: string | number) => `${WAGONS_BASE}/${id}`,
    DELETE: (id: string | number) => `${WAGONS_BASE}/${id}`,
    BOGIES: (id: string | number) => `${WAGONS_BASE}/${id}/bogies`,
    WHEELS: (id: string | number) => `${WAGONS_BASE}/${id}/wheels`,
    GEARBOX: (id: string | number) => `${WAGONS_BASE}/${id}/gearbox`,
    EQUIPMENT: (id: string | number) => `${WAGONS_BASE}/${id}/equipment`,
    ALARMS: (id: string | number) => `${WAGONS_BASE}/${id}/alarms`,
    DIAGNOSTICS: (id: string | number) => `${WAGONS_BASE}/${id}/diagnostics`
  },

  // Fleet endpoints (mevcut projende FleetDetailPage, FleetListPage var)
  FLEETS: {
    BASE: FLEETS_BASE,
    LIST: FLEETS_BASE,
    DETAIL: (id: string | number) => `${FLEETS_BASE}/${id}`,
    CREATE: FLEETS_BASE,
    UPDATE: (id: string | number) => `${FLEETS_BASE}/${id}`,
    DELETE: (id: string | number) => `${FLEETS_BASE}/${id}`,
    UNITS: (id: string | number) => `${FLEETS_BASE}/${id}/units`,
    WAGONS: (id: string | number) => `${FLEETS_BASE}/${id}/wagons`,
    STATISTICS: (id: string | number) => `${FLEETS_BASE}/${id}/statistics`,
    PERFORMANCE: (id: string | number) => `${FLEETS_BASE}/${id}/performance`
  },

  // User management (mevcut projende UserListPage, permissions var)
  USERS: {
    BASE: USERS_BASE,
    LIST: USERS_BASE,
    DETAIL: (id: string | number) => `${USERS_BASE}/${id}`,
    CREATE: USERS_BASE,
    UPDATE: (id: string | number) => `${USERS_BASE}/${id}`,
    DELETE: (id: string | number) => `${USERS_BASE}/${id}`,
    ROLES: `${USERS_BASE}/roles`,
    PERMISSIONS: `${USERS_BASE}/permissions`,
    TENANT_USERS: (tenantId: string | number) => `${TENANTS_BASE}/${tenantId}/users`
  },

  // Tenant management (mevcut projende TenantDetailPage var)
  TENANTS: {
    BASE: TENANTS_BASE,
    LIST: TENANTS_BASE,
    DETAIL: (id: string | number) => `${TENANTS_BASE}/${id}`,
    CREATE: TENANTS_BASE,
    UPDATE: (id: string | number) => `${TENANTS_BASE}/${id}`,
    DELETE: (id: string | number) => `${TENANTS_BASE}/${id}`,
    FLEETS: (id: string | number) => `${TENANTS_BASE}/${id}/fleets`,
    SETTINGS: (id: string | number) => `${TENANTS_BASE}/${id}/settings`,
    STATISTICS: (id: string | number) => `${TENANTS_BASE}/${id}/statistics`
  },

  // Buildings (mevcut projende buildingsApi.ts var)
  BUILDINGS: {
    BASE: BUILDINGS_BASE,
    LIST: BUILDINGS_BASE,
    DETAIL: (id: string | number) => `${BUILDINGS_BASE}/${id}`,
    CREATE: BUILDINGS_BASE,
    UPDATE: (id: string | number) => `${BUILDINGS_BASE}/${id}`,
    DELETE: (id: string | number) => `${BUILDINGS_BASE}/${id}`,
    UNITS: (id: string | number) => `${BUILDINGS_BASE}/${id}/units`,
    STATISTICS: (id: string | number) => `${BUILDINGS_BASE}/${id}/statistics`
  },

  // Notifications (mevcut projende Notifications page var)
  NOTIFICATIONS: {
    BASE: NOTIFICATIONS_BASE,
    LIST: NOTIFICATIONS_BASE,
    UNREAD: `${NOTIFICATIONS_BASE}/unread`,
    MARK_AS_READ: (id: string | number) => `${NOTIFICATIONS_BASE}/${id}/read`,
    MARK_ALL_READ: `${NOTIFICATIONS_BASE}/mark-all-read`,
    SETTINGS: `${NOTIFICATIONS_BASE}/settings`,
    PREFERENCES: `${NOTIFICATIONS_BASE}/preferences`
  },

  // System & Health
  SYSTEM: {
    HEALTH: `/health`,
    METRICS: `/metrics`,
    VERSION: `/version`,
    CONFIG: `/config`
  }
} as const;

// Type definitions for endpoints
export type Endpoints = typeof ENDPOINTS;
export type AuthEndpoints = Endpoints['AUTH'];
export type AlarmEndpoints = Endpoints['ALARMS'];
export type UnitEndpoints = Endpoints['UNITS'];

// Helper function to create endpoint with params
export const createEndpoint = <T extends string | ((...args: any[]) => string)>(
  endpoint: T,
  ...args: T extends (...args: infer P) => string ? P : never[]
): string => {
  return typeof endpoint === 'function' ? endpoint(...args) : endpoint;
};

// Example usage:
// const alarmDetailUrl = createEndpoint(ENDPOINTS.ALARMS.DETAIL, '123');
// const unitWagonsUrl = createEndpoint(ENDPOINTS.UNITS.WAGONS, '456');